import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import LiveRegion from '../components/common/LiveRegion';

interface AnnounceContextValue {
  /** Pousse un message dans la région live (lecteurs d'écran, WCAG 4.1.3). */
  announce: (message: string) => void;
}

const AnnounceContext = createContext<AnnounceContextValue | null>(null);

/**
 * Provider de la région live ARIA — à monter UNE SEULE fois (dans le layout).
 * Rend `children` + une `<LiveRegion>` unique pilotée par `announce()`.
 *
 * Astuce relecture : on suffixe un caractère invisible (zero-width space) qui
 * alterne à chaque appel, pour que deux annonces identiques successives soient
 * tout de même perçues comme un changement de contenu par le lecteur d'écran.
 */
export function AnnounceProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState('');

  const announce = useCallback((next: string) => {
    setMessage((prev) =>
      prev === next ? `${next}​` : next
    );
  }, []);

  const value = useMemo<AnnounceContextValue>(() => ({ announce }), [announce]);

  return (
    <AnnounceContext.Provider value={value}>
      {children}
      <LiveRegion message={message} />
    </AnnounceContext.Provider>
  );
}

/**
 * Accès à `announce()` pour pousser un message vocal poli (non bloquant).
 * No-op silencieux si appelé hors provider (jamais censé arriver en prod).
 */
export function useAnnounce(): AnnounceContextValue {
  const ctx = useContext(AnnounceContext);
  if (!ctx) {
    return { announce: () => { /* hors provider : no-op */ } };
  }
  return ctx;
}
